import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { ArrowRight, CalendarClock, CarFront } from "lucide-react";

import { Countdown, StageBadge } from "@/components/portal/portal-ui";
import { Button } from "@/components/ui/button";
import type { BookingStage } from "@/lib/booking-stages";
import { listMyBookings } from "@/lib/bookings.functions";
import { useSupabaseUser } from "@/lib/use-supabase-user";

export const Route = createFileRoute("/_authenticated/portal/")({
  head: () => ({
    meta: [
      { title: "My Bookings | Car Rental Bucharest" },
      {
        name: "description",
        content:
          "Your Car Rental Bucharest customer portal: booking status, countdown to pickup and return, rental details and support.",
      },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "My Bookings | Car Rental Bucharest" },
      { property: "og:description", content: "Track your Bucharest car rental bookings." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: PortalDashboard,
});

function PortalDashboard() {
  const { user } = useSupabaseUser();
  const fetchBookings = useServerFn(listMyBookings);
  const query = useQuery({ queryKey: ["my-bookings"], queryFn: () => fetchBookings({}) });

  const today = new Date().toISOString().slice(0, 10);
  const all = query.data ?? [];
  const upcoming = all.filter((b) => b.return_date >= today);
  const past = all.filter((b) => b.return_date < today);
  const next = upcoming[upcoming.length - 1];

  return (
    <>
      <main className="container-page py-10 sm:py-14">
        <header className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold">
              Customer portal
            </p>
            <h1 className="mt-2 font-display text-3xl font-bold sm:text-4xl">
              Welcome{user?.name ? `, ${user.name}` : ""}
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">{user?.email}</p>
          </div>
          <Button asChild variant="outline">
            <Link to="/rentals">
              Browse the fleet <ArrowRight className="ml-2 size-4" />
            </Link>
          </Button>
        </header>

        {query.isLoading ? (
          <p className="mt-10 text-muted-foreground">Loading your bookings…</p>
        ) : all.length === 0 ? (
          <div className="mt-10 rounded-xl border border-border bg-card p-8 text-center">
            <CarFront className="mx-auto size-8 text-gold" />
            <h2 className="mt-3 font-display text-xl font-bold">No bookings yet</h2>
            <p className="mx-auto mt-2 max-w-sm text-sm text-muted-foreground">
              When you reserve a car, it appears here with live status, countdowns and pickup
              details.
            </p>
            <Button asChild className="mt-5">
              <Link to="/rentals">Find your car</Link>
            </Button>
          </div>
        ) : (
          <>
            {next ? (
              <div className="mt-8 grid gap-4 sm:grid-cols-2">
                <Countdown label="Pickup in" date={next.pickup_date} doneLabel="Pickup day" />
                <Countdown label="Return in" date={next.return_date} doneLabel="Return due" />
              </div>
            ) : null}

            <BookingList title="Upcoming & active rentals" bookings={upcoming} />
            <BookingList title="Previous rentals" bookings={past} />
          </>
        )}
      </main>
    </>
  );
}

interface BookingRow {
  id: string;
  car_name: string;
  pickup_date: string;
  return_date: string;
  pickup_location: string;
  total_estimate: number | null;
  stage: BookingStage;
}

function BookingList({ title, bookings }: { title: string; bookings: BookingRow[] }) {
  if (bookings.length === 0) return null;
  return (
    <section className="mt-10">
      <h2 className="font-display text-xl font-bold">{title}</h2>
      <ul className="mt-4 space-y-3">
        {bookings.map((b) => (
          <li key={b.id}>
            <Link
              to="/portal/$id"
              params={{ id: b.id }}
              className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-border bg-card p-5 transition-colors hover:border-gold/50"
            >
              <div className="min-w-0">
                <p className="font-display text-base font-bold">{b.car_name}</p>
                <p className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
                  <CalendarClock className="size-4" />
                  {b.pickup_date} → {b.return_date}
                </p>
                <p className="mt-1 truncate text-xs text-muted-foreground">{b.pickup_location}</p>
              </div>
              <div className="flex items-center gap-3">
                {b.total_estimate ? (
                  <span className="font-display text-lg font-bold text-gold">
                    €{Number(b.total_estimate)}
                  </span>
                ) : null}
                <StageBadge stage={b.stage} />
                <ArrowRight className="size-4 text-muted-foreground" />
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
